import { useErrorModal } from "@core/composables/state/useErrorModal";
import { useLanguage } from "@core/composables/state/useLanguage";
import type { PaymentSource, PlusSubscription, UserDeviceOrigin, UserProfile, Vehicle } from "@core/types/models";

import { useTranslate } from "@common/composables/useTranslate";
import { resolveSupportedLanguage } from "@common/i18n";

interface Permissions {
  statistics: boolean;
  telemetry: boolean;
  applications: boolean;
  users: boolean;
  organizations: boolean;
  audits: boolean;
  remits: boolean;
  vehicleRegistry: boolean;
  manualApproval: boolean;
  trafficControl: boolean;
  camerasOverview: boolean;
  system: boolean;
  youparkTodos: boolean;
  youparkCustomerSubscriptionsRead: boolean;
  youparkSubscriptions: boolean;
  youparkInvoice: boolean;
  youparkInvoiceWrite: boolean;
  youpayAccounting: boolean;
  youpayRefund: boolean;
  youparkFreeVisitRead: boolean;
  youparkAccounting: boolean;
  access: boolean;
  youparkSettings: boolean;
}

interface Lot {
  id: string;
  organizationId: string;
  organizationName: string;
}

interface CookieNamesConfig {
  authentication: string;
  language: string;
}

interface AppConfig {
  isAuthenticated: boolean;
  userProfile: UserProfile;
  isEmployee: boolean;
  isImpersonatedUser: boolean;
  isNativeClient: boolean;
  permissions: Permissions;
  canAccessYouParkCustomer: boolean;
  isYouParkCustomer: boolean;
  lots: Lot[];
  vehicles?: Vehicle[];
  paymentSources?: PaymentSource[];
  languageCookieTag?: string | null;
  publicViews: string[];
  cookieNames: CookieNamesConfig;
}

const defaultPermissions: Permissions = {
  statistics: false,
  telemetry: false,
  applications: false,
  users: false,
  organizations: false,
  audits: false,
  remits: false,
  vehicleRegistry: false,
  manualApproval: false,
  trafficControl: false,
  system: false,
  camerasOverview: false,
  youparkTodos: false,
  youparkCustomerSubscriptionsRead: false,
  youparkSubscriptions: false,
  youparkInvoice: false,
  youparkInvoiceWrite: false,
  youpayAccounting: false,
  youpayRefund: false,
  youparkFreeVisitRead: false,
  youparkAccounting: false,
  access: false,
  youparkSettings: false
};

const defaultLanguage = resolveSupportedLanguage(document.documentElement.lang);

const anonymousProfile: UserProfile = {
  firstName: null,
  middleName: null,
  lastName: null,
  fullName: null,
  email: "",
  registeredDate: "",
  phoneNumber: "",
  languageTag: defaultLanguage,
  emailTransactionsAfterPurchase: false,
  analyticsEnabled: false,
  customerContactInfoEnabled: false,
  shouldShowContactInfoConfirmation: false,
  devices: []
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function toStringOrNull(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function toStringOrEmpty(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function normalizeDeviceOrigin(value: unknown): UserDeviceOrigin {
  if (typeof value !== "string") {
    return "browser";
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === "android" || normalized === "ios" || normalized === "browser") {
    return normalized;
  }

  return "browser";
}

function normalizeDevices(raw: unknown): UserProfile["devices"] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const devices: UserProfile["devices"] = [];
  raw.forEach(device => {
    if (!isRecord(device)) {
      return;
    }
    devices.push({
      id: toStringOrEmpty(device.id),
      ipAddress: toStringOrEmpty(device.ipAddress),
      browser: toStringOrEmpty(device.browser),
      os: toStringOrEmpty(device.os),
      origin: normalizeDeviceOrigin(device.origin),
      country: toStringOrEmpty(device.country),
      signedInAt: toStringOrEmpty(device.signedInAt),
      confirmedAt: typeof device.confirmedAt === "string" ? device.confirmedAt : null
    });
  });
  return devices;
}

function normalizeUserProfile(raw: unknown): UserProfile {
  if (!isRecord(raw)) {
    return { ...anonymousProfile };
  }

  const profile: UserProfile = {
    firstName: toStringOrNull(raw.firstName),
    middleName: toStringOrNull(raw.middleName),
    lastName: toStringOrNull(raw.lastName),
    fullName: toStringOrNull(raw.fullName),
    email: toStringOrEmpty(raw.email),
    registeredDate: toStringOrEmpty(raw.registeredDate),
    phoneNumber: toStringOrEmpty(raw.phoneNumber),
    languageTag: toStringOrEmpty(raw.languageTag) || defaultLanguage,
    emailTransactionsAfterPurchase: raw.emailTransactionsAfterPurchase === true,
    analyticsEnabled: raw.analyticsEnabled === true,
    customerContactInfoEnabled: raw.customerContactInfoEnabled === true,
    shouldShowContactInfoConfirmation: raw.shouldShowContactInfoConfirmation === true,
    devices: normalizeDevices(raw.devices)
  };

  if (isRecord(raw.plusSubscription)) {
    const plusSubscription = {
      validUntilDate:
        typeof raw.plusSubscription.validUntilDate === "string" ? raw.plusSubscription.validUntilDate : undefined,
      expiredDate: typeof raw.plusSubscription.expiredDate === "string" ? raw.plusSubscription.expiredDate : undefined,
      automaticRenewalEnabled: raw.plusSubscription.automaticRenewalEnabled === true
    } satisfies PlusSubscription;
    profile.plusSubscription = plusSubscription;
  }

  return profile;
}

function normalizePermissions(raw: unknown): Permissions {
  if (!isRecord(raw)) {
    return { ...defaultPermissions };
  }

  return {
    statistics: raw.statistics === true,
    telemetry: raw.telemetry === true,
    applications: raw.applications === true,
    users: raw.users === true,
    organizations: raw.organizations === true,
    audits: raw.audits === true,
    remits: raw.remits === true,
    vehicleRegistry: raw.vehicleRegistry === true,
    manualApproval: raw.manualApproval === true,
    trafficControl: raw.trafficControl === true,
    system: raw.system === true,
    camerasOverview: raw.camerasOverview === true,
    youparkTodos: raw.youparkTodos === true,
    youparkCustomerSubscriptionsRead: raw.youparkCustomerSubscriptionsRead === true,
    youparkSubscriptions: raw.youparkSubscriptions === true,
    youparkInvoice: raw.youparkInvoice === true,
    youparkInvoiceWrite: raw.youparkInvoiceWrite === true,
    youpayAccounting: raw.youpayAccounting === true,
    youpayRefund: raw.youpayRefund === true,
    youparkFreeVisitRead: raw.youparkFreeVisitRead === true,
    youparkAccounting: raw.youparkAccounting === true,
    access: raw.access === true,
    youparkSettings: raw.youparkSettings === true
  };
}

function normalizeLots(raw: unknown): Lot[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const lots: Lot[] = [];
  raw.forEach(item => {
    if (!isRecord(item)) {
      return;
    }
    lots.push({
      id: toStringOrEmpty(item.id),
      organizationId: toStringOrEmpty(item.organizationId),
      organizationName: toStringOrEmpty(item.organizationName)
    });
  });
  return lots;
}

function normalizeCookieNames(raw: unknown): CookieNamesConfig {
  if (!isRecord(raw)) {
    return { authentication: "", language: "" };
  }

  return {
    authentication: toStringOrEmpty(raw.authentication),
    language: toStringOrEmpty(raw.language)
  };
}

function createAnonymousConfig(
  languageCookieTag?: string | null,
  publicViews?: string[],
  cookieNamesRaw?: unknown
): AppConfig {
  return {
    isAuthenticated: false,
    userProfile: { ...anonymousProfile },
    isEmployee: false,
    isImpersonatedUser: false,
    isNativeClient: false,
    permissions: { ...defaultPermissions },
    canAccessYouParkCustomer: false,
    isYouParkCustomer: false,
    lots: [],
    vehicles: [],
    paymentSources: [],
    languageCookieTag: languageCookieTag ?? null,
    publicViews: Array.isArray(publicViews) ? publicViews : [],
    cookieNames: normalizeCookieNames(cookieNamesRaw)
  };
}

function normalizePublicViews(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((value): value is string => typeof value === "string")
    .map(value => value.trim())
    .filter(value => value.length > 0);
}

function normalizeConfig(raw: unknown): AppConfig {
  if (!isRecord(raw)) {
    return createAnonymousConfig();
  }

  const languageCookieTagValue = toStringOrEmpty(raw.languageCookieTag);
  const normalizedLanguageCookieTag = languageCookieTagValue.length > 0 ? languageCookieTagValue : null;
  const isAuthenticated = raw.isAuthenticated !== false;
  const publicViews = normalizePublicViews(raw.publicViews);
  if (!isAuthenticated) {
    return createAnonymousConfig(normalizedLanguageCookieTag, publicViews, raw.cookieNames);
  }

  return {
    isAuthenticated: true,
    userProfile: normalizeUserProfile(raw.userProfile),
    isEmployee: raw.isEmployee === true,
    isImpersonatedUser: raw.isImpersonatedUser === true,
    isNativeClient: raw.isNativeClient === true,
    permissions: normalizePermissions(raw.permissions),
    canAccessYouParkCustomer: raw.canAccessYouParkCustomer === true,
    isYouParkCustomer: raw.isYouParkCustomer === true,
    lots: normalizeLots(raw.lots),
    vehicles: Array.isArray(raw.vehicles) ? (raw.vehicles as Vehicle[]) : [],
    paymentSources: Array.isArray(raw.paymentSources) ? (raw.paymentSources as PaymentSource[]) : [],
    languageCookieTag: normalizedLanguageCookieTag,
    publicViews,
    cookieNames: normalizeCookieNames(raw.cookieNames)
  };
}

function loadConfigFromDOM(): unknown {
  const configElement = document.getElementById("dashboard-config");
  if (!configElement?.textContent) {
    throw new Error("Application configuration not found in DOM");
  }

  try {
    return JSON.parse(configElement.textContent);
  } catch (err) {
    console.error("Failed to parse app config from DOM:", err);
    throw new Error("Invalid application configuration");
  }
}

const appConfig: AppConfig = normalizeConfig(loadConfigFromDOM());

export const maybeTurnstileSiteKey: string | undefined = document.getElementById(
  "cloudflare-turnstile-site-key"
)?.textContent;

if (!maybeTurnstileSiteKey) {
  throw new Error("Turnstile site key not found in DOM");
}

export const turnstileSiteKey: string = maybeTurnstileSiteKey;

try {
  const { setLanguage, normalizeLanguageTag, toLanguageTag } = useLanguage();
  const cookieLanguage = appConfig.languageCookieTag ? normalizeLanguageTag(appConfig.languageCookieTag) : null;
  const language = cookieLanguage ?? normalizeLanguageTag(appConfig.userProfile.languageTag);
  setLanguage(language);
  appConfig.userProfile.languageTag = toLanguageTag(language);
  appConfig.languageCookieTag = toLanguageTag(language);
} catch (error: unknown) {
  let resolvedMessage: string | null = null;
  if (error instanceof Error && typeof error.message === "string") {
    resolvedMessage = error.message;
  } else if (typeof error === "string") {
    resolvedMessage = error;
  }
  const logMessage = resolvedMessage ?? "Unknown error";
  console.error("Error setting language:", logMessage);
  const translateFn = useTranslate();
  useErrorModal().showError(translateFn("common.errorLanguageInit"), translateFn("common.errorTitle"));
}

export function useAppConfig() {
  const translate = useTranslate();
  const translateKey = (key: string | undefined, fallback?: string) => {
    if (!key) {
      return fallback ?? "";
    }
    const value = translate(key);
    return value !== key ? value : (fallback ?? key);
  };

  return {
    config: appConfig,
    userProfile: appConfig.userProfile,
    translate,
    translateKey,
    turnstileSiteKey
  } as const;
}
